import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Dropdown } from 'antd';
import {
  PlusOutlined, RobotOutlined, BranchesOutlined, UserOutlined,
  FileSearchOutlined, TagsOutlined, DatabaseOutlined,
  ApiOutlined, CalculatorOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { FlowNode, VisualNodeType, ConnectorLine, CARD_WIDTH, CARD_HEIGHT, V_GAP } from '../types/flow';
import { layoutFlow } from '../utils/layout';
import FlowNodeCard from './FlowNodeCard';

interface FlowCanvasProps {
  nodes: FlowNode[];
  selectedNodeId: string | null;
  onNodeClick: (node: FlowNode) => void;
  onDeleteNode: (nodeId: string) => void;
  onAddNode: (type: VisualNodeType, afterNodeId: string) => void;
  onAddBranchNode: (type: VisualNodeType, conditionNodeId: string, branchIndex: number) => void;
}

const ADD_NODE_ITEMS: MenuProps['items'] = [
  { key: 'lead_enrichment', label: 'Buscar Lead', icon: <UserOutlined style={{ color: '#fa8c16' }} /> },
  { key: 'estimate_budget', label: 'Calcular Presupuesto', icon: <CalculatorOutlined style={{ color: '#a0d911' }} /> },
  { key: 'http_request', label: 'Llamada HTTP', icon: <ApiOutlined style={{ color: '#0958d9' }} /> },
  { type: 'divider' },
  { key: 'llm', label: 'LLM', icon: <RobotOutlined style={{ color: '#1677ff' }} /> },
  { key: 'extraction', label: 'Extracción', icon: <FileSearchOutlined style={{ color: '#722ed1' }} /> },
  { key: 'classification', label: 'Clasificación', icon: <TagsOutlined style={{ color: '#13c2c2' }} /> },
  { key: 'condition', label: 'Condición', icon: <BranchesOutlined style={{ color: '#faad14' }} /> },
  { key: 'human_input', label: 'Input Humano', icon: <UserOutlined style={{ color: '#eb2f96' }} /> },
  { key: 'bigquery_save', label: 'Guardar BigQuery', icon: <DatabaseOutlined style={{ color: '#52c41a' }} /> },
];

const FlowCanvas: React.FC<FlowCanvasProps> = ({
  nodes,
  selectedNodeId,
  onNodeClick,
  onDeleteNode,
  onAddNode,
  onAddBranchNode,
}) => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const offsetStart = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('.flow-node-card, .add-node-btn, .ant-dropdown, .node-delete-btn')) return;
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      offsetStart.current = { ...offset };
    },
    [offset]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setOffset({
        x: offsetStart.current.x + dx,
        y: offsetStart.current.y + dy,
      });
    },
    [dragging]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  const layout = useMemo(() => layoutFlow(nodes), [nodes]);

  const renderConnectors = () => (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: layout.totalWidth + 200,
        height: layout.totalHeight + 200,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="8"
          markerHeight="6"
          refX="7"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="#c0c0c0" />
        </marker>
      </defs>
      {layout.connectors.map((conn: ConnectorLine) => {
        const { from, to } = conn;
        const midX = (from.x + to.x) / 2;
        const d = `M ${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`;
        return (
          <g key={conn.id}>
            <path
              d={d}
              fill="none"
              stroke="#d0d0d0"
              strokeWidth={2}
              markerEnd="url(#arrowhead)"
            />
            {conn.label && (
              <text
                x={(from.x + to.x) / 2}
                y={(from.y + to.y) / 2 - 8}
                textAnchor="middle"
                fontSize={11}
                fill="#8c8c8c"
                fontFamily="Inter, -apple-system, sans-serif"
              >
                {conn.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );

  const renderAddButtons = () => {
    const buttons: React.ReactNode[] = [];

    const addForSequence = (
      nodeList: FlowNode[],
      isBranch: boolean,
      conditionNodeId?: string,
      branchIndex?: number,
    ) => {
      for (let i = 0; i < nodeList.length; i++) {
        const node = nodeList[i];
        const item = layout.items.find((it) => it.node.id === node.id);
        if (!item) continue;

        const isLast = i === nodeList.length - 1;
        if (isLast) {
          const btnX = item.x + CARD_WIDTH + (100 - 28) / 2;
          const btnY = item.y + CARD_HEIGHT / 2 - 14;

          buttons.push(
            <Dropdown
              key={`add-${node.id}`}
              menu={{
                items: ADD_NODE_ITEMS,
                onClick: ({ key }) => {
                  if (isBranch && conditionNodeId !== undefined && branchIndex !== undefined) {
                    onAddBranchNode(key as VisualNodeType, conditionNodeId, branchIndex);
                  } else {
                    onAddNode(key as VisualNodeType, node.id);
                  }
                },
              }}
              trigger={['click']}
            >
              <button
                className="add-node-btn"
                style={{
                  position: 'absolute',
                  left: btnX,
                  top: btnY,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: '2px dashed #d0d0d0',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  color: '#bfbfbf',
                  transition: 'all 0.2s',
                  zIndex: 3,
                  padding: 0,
                }}
              >
                <PlusOutlined />
              </button>
            </Dropdown>
          );
        }

        if (node.type === 'condition' && node.branches) {
          for (let bi = 0; bi < node.branches.length; bi++) {
            if (node.branches[bi].length === 0) {
              const condItem = layout.items.find((it) => it.node.id === node.id);
              if (condItem) {
                const branchCount = node.branches.length;
                const totalBranchSpan = branchCount * CARD_HEIGHT + (branchCount - 1) * V_GAP;
                const condCenterY = condItem.y + CARD_HEIGHT / 2;
                const branchTopY = condCenterY - totalBranchSpan / 2;
                const branchY = branchTopY + bi * (CARD_HEIGHT + V_GAP) + CARD_HEIGHT / 2 - 14;

                buttons.push(
                  <Dropdown
                    key={`add-branch-${node.id}-${bi}`}
                    menu={{
                      items: ADD_NODE_ITEMS,
                      onClick: ({ key }) => {
                        onAddBranchNode(key as VisualNodeType, node.id, bi);
                      },
                    }}
                    trigger={['click']}
                  >
                    <button
                      className="add-node-btn"
                      style={{
                        position: 'absolute',
                        left: condItem.x + CARD_WIDTH + 100 + (100 - 28) / 2,
                        top: branchY,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px dashed #d0d0d0',
                        background: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        color: '#bfbfbf',
                        transition: 'all 0.2s',
                        zIndex: 3,
                        padding: 0,
                      }}
                    >
                      <PlusOutlined />
                    </button>
                  </Dropdown>
                );
              }
            } else {
              addForSequence(node.branches[bi], true, node.id, bi);
            }
          }
        }
      }
    };

    addForSequence(nodes, false);
    return buttons;
  };

  return (
    <div
      style={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        cursor: dragging ? 'grabbing' : 'grab',
        background: '#fafbfc',
        backgroundImage:
          'radial-gradient(circle, #e0e0e0 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          position: 'relative',
          width: layout.totalWidth + 200,
          height: layout.totalHeight + 200,
        }}
      >
        {renderConnectors()}

        {layout.items.map((item) => (
          <FlowNodeCard
            key={item.node.id}
            node={item.node}
            x={item.x}
            y={item.y}
            selected={selectedNodeId === item.node.id}
            onClick={onNodeClick}
            onDelete={onDeleteNode}
          />
        ))}

        {renderAddButtons()}
      </div>
    </div>
  );
};

export default FlowCanvas;
